import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /*
   * 3NODE — záměr myši (Oldřich, Figma 11. 9. 2026): menu se muselo „dlouho
   * čekat" a při šikmém tahu k dlaždici na kraji panelu se po cestě zavřelo.
   * Téma zavíralo okamžitě při opuštění položky a přepínalo až po zastavení
   * myši (bezpečná zóna přes celý řádek hlavičky, jen během pohybu).
   * Nově: přepnutí po krátké prodlevě, zavření se zpožděním, a dokud myš
   * v řádku hlavičky míří dolů k panelu, nic se nepřepne ani nezavře.
   * Klávesnice a dotyk jedou původní cestou. Test: scripts/qa/menu-hover.mjs.
   */
  static CLOSE_DELAY = 300;
  static SWITCH_DELAY = 80;
  static AIM_DELAY = 300;
  /** Jak dlouho musí myš stát, než se zastavení při míření k panelu bere vážně. */
  static AIM_IDLE = 350;
  /** Rychlejší vodorovný průjezd (px/ms) panely nepřepíná — jinak blikají. */
  static FAST_SWEEP = 0.9;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #closeTimer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #switchTimer;

  /** Poslední pozice a vyhlazený směr pohybu myši. */
  #intent = { x: 0, y: 0, dx: 0, dy: 0, v: 0, t: 0 };

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    clearTimeout(this.#closeTimer);
    clearTimeout(this.#switchTimer);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    // 3NODE: bezpečná zóna tématu maskovala sousední položky (přepnutí až po
    // zastavení) a po 50 ms klidu zmizela (zavření cestou). Nahrazuje ji
    // záměr myši níž — tady se jen sleduje směr pohybu.
    if (event.pointerType === 'mouse') {
      this.#trackIntent(event);
      return;
    }

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    clearTimeout(this.#closeTimer);
    this.#closeTimer = undefined;
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);
  };

  /**
   * 3NODE — najetí myší na položku nejvyšší úrovně.
   * @param {PointerEvent} event
   */
  pointerEnter = (event) => {
    const target = event.target;
    if (event.pointerType !== 'mouse' || !(target instanceof Element) || target.hasAttribute('slot')) {
      this.activate(event);
      return;
    }

    this.#trackIntent(event);
    clearTimeout(this.#switchTimer);
    this.#switchTimer = undefined;

    const item = findMenuItem(target);
    const active = this.#state.activeItem;
    if (!active || !item || item === active) {
      this.activate(event);
      return;
    }

    // Jiná položka je otevřená: přepnout až po krátké prodlevě; když myš míří
    // k panelu (projíždí přes sousední položku), počkat, až se zastaví.
    clearTimeout(this.#closeTimer);
    this.#closeTimer = undefined;
    /** @param {number} delay */
    const trySwitch = (delay) => {
      this.#switchTimer = setTimeout(() => {
        this.#switchTimer = undefined;
        if (!target.matches(':hover')) return;
        const idle = performance.now() - this.#intent.t;
        // Mířila k panelu a jen se zarazila — přepnout až po delším klidu.
        if (this.#isAimingAtPanel() && idle < HeaderMenu.AIM_IDLE) {
          trySwitch(HeaderMenu.AIM_IDLE - idle);
          return;
        }
        // Rychlý průjezd přes menu — počkat, až myš zpomalí.
        if (this.#intent.v > HeaderMenu.FAST_SWEEP && idle < 40) {
          trySwitch(40);
          return;
        }
        this.activate(event);
      }, delay);
    };
    trySwitch(this.#isAimingAtPanel() ? HeaderMenu.AIM_DELAY : HeaderMenu.SWITCH_DELAY);
  };

  /**
   * 3NODE — odjetí myší z položky nejvyšší úrovně (i s jejím panelem).
   * @param {PointerEvent} event
   */
  pointerLeave = (event) => {
    const target = event.target;
    if (event.pointerType !== 'mouse' || !(target instanceof Element) || target.hasAttribute('slot')) {
      this.deactivate(event);
      return;
    }

    if (this.#switchTimer && !target.matches(':hover')) {
      clearTimeout(this.#switchTimer);
      this.#switchTimer = undefined;
    }

    const active = this.#state.activeItem;
    if (!active) return;
    const activeListItem = active.closest('.menu-list__list-item');
    if (activeListItem?.matches(':hover')) return;
    this.#scheduleClose();
  };

  #scheduleClose() {
    clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(() => {
      this.#closeTimer = undefined;
      const active = this.#state.activeItem;
      if (!active) return;
      // Kurzor je zpátky v položce nebo v panelu, nebo se čeká na přepnutí.
      if (active.closest('.menu-list__list-item')?.matches(':hover') || this.#switchTimer) return;
      // Jela v řádku hlavičky dolů k panelu a nestojí dost dlouho — počkat.
      const idle = performance.now() - this.#intent.t;
      if (this.#isAimingAtPanel() && idle < HeaderMenu.AIM_IDLE) {
        clearTimeout(this.#closeTimer);
        this.#closeTimer = setTimeout(() => {
          this.#closeTimer = undefined;
          const current = this.#state.activeItem;
          if (!current || current.closest('.menu-list__list-item')?.matches(':hover') || this.#switchTimer) return;
          if (performance.now() - this.#intent.t < HeaderMenu.AIM_IDLE) {
            this.#scheduleClose();
            return;
          }
          this.#deactivate(current);
        }, HeaderMenu.AIM_IDLE - idle);
        return;
      }
      this.#deactivate(active);
    }, HeaderMenu.CLOSE_DELAY);
  }

  /** @param {PointerEvent} event */
  #trackIntent(event) {
    const i = this.#intent;
    const now = performance.now();
    const dt = now - i.t;
    const fresh = dt > 150;
    const dx = fresh ? 0 : event.clientX - i.x;
    const dy = fresh ? 0 : event.clientY - i.y;
    // Po pauze se směr nenuluje — zastavení při míření k panelu ho má zachovat.
    if (!fresh) {
      i.dx = i.dx * 0.6 + dx * 0.4;
      i.dy = i.dy * 0.6 + dy * 0.4;
      i.v = i.v * 0.6 + (Math.hypot(dx, dy) / Math.max(dt, 1)) * 0.4;
    }
    i.x = event.clientX;
    i.y = event.clientY;
    i.t = now;
  }

  /** Míří myš z řádku hlavičky dolů k otevřenému panelu? */
  #isAimingAtPanel() {
    if (!this.#state.activeItem || !this.headerComponent) return false;
    const { y, dx, dy } = this.#intent;
    const headerBottom = this.headerComponent.getBoundingClientRect().bottom;
    return y < headerBottom && dy > 0.05 && dy >= Math.abs(dx) * 0.08;
  }

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
